import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import PageHelmet from '@/components/feature/PageHelmet';
import { aboutData, contactFaqData } from '@/mocks/about';
import { useState } from 'react';

export default function AboutPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于鬼父在线',
    description: aboutData.brand.description,
    url: 'https://balnsj.cn/about',
    about: {
      '@type': 'Organization',
      name: '鬼父在线',
      description: aboutData.brand.description,
      email: aboutData.contact.email,
      telephone: aboutData.contact.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: aboutData.contact.address,
        addressCountry: 'CN',
      },
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: contactFaqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <PageHelmet
        title="关于我们 - 鬼父在线"
        description="鬼父在线 - 正版高清资源直达平台。了解鬼父在线的品牌故事、发展历程、荣誉资质及联系方式。"
        keywords="鬼父在线,关于鬼父在线,鬼父在线联系方式,鬼父在线品牌"
        canonicalUrl="https://balnsj.cn/about"
        jsonLd={[aboutJsonLd, faqJsonLd]}
      />

      <Navbar />

      <main className="pt-16">
        <div className="w-full max-w-6xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Introduction */}
          <section className="mb-16">
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  {aboutData.brand.title}
                </h1>
                <p className="text-primary-600 font-heading text-lg mb-5">
                  {aboutData.brand.subtitle}
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {aboutData.brand.description}
                </p>
                <div className="space-y-3 mt-5">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 flex-shrink-0 mt-0.5">
                      <i className="ri-focus-3-line text-primary-600 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-sm text-foreground-800">我们的使命</strong>
                      <p className="text-sm text-foreground-500 mt-0.5">{aboutData.brand.mission}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100 flex-shrink-0 mt-0.5">
                      <i className="ri-eye-line text-accent-600 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-sm text-foreground-800">我们的愿景</strong>
                      <p className="text-sm text-foreground-500 mt-0.5">{aboutData.brand.vision}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-1 w-full">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src={aboutData.brand.image}
                    alt="关于鬼父在线"
                    className="w-full h-auto object-cover"
                    title="鬼父在线 - 关于我们"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Features Grid */}
          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              我们的优势
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {aboutData.features.map((feature) => (
                <div key={feature.title} className="text-center p-4 rounded-xl bg-background-100">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${feature.icon} text-lg`}></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-800 mb-1">{feature.title}</h4>
                  <p className="text-xs text-foreground-500">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Development History */}
          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              发展历程
            </h2>
            <div className="max-w-2xl mx-auto">
              <div className="relative pl-8 border-l-2 border-primary-200">
                {aboutData.history.map((item) => (
                  <div key={item.year} className="mb-8 relative">
                    <div className="absolute -left-[41px] top-0 w-4 h-4 rounded-full bg-primary-500 border-4 border-background-50"></div>
                    <span className="inline-block text-sm font-bold text-primary-600 bg-primary-50 px-3 py-0.5 rounded-full mb-2">
                      {item.year}
                    </span>
                    <p className="text-sm text-foreground-600">{item.event}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Honors */}
          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              荣誉资质
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {aboutData.honors.map((honor) => (
                <div key={honor.title} className="p-5 rounded-xl bg-background-100 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-medal-line text-xl text-accent-600"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-800 mb-1">{honor.title}</h4>
                  <p className="text-xs text-foreground-500">{honor.issuer}</p>
                  <span className="inline-block mt-2 text-xs text-accent-600 font-medium">{honor.year}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Information */}
          <section id="contact" className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              {aboutData.contact.title}
            </h2>
            <div className="max-w-3xl mx-auto">
              <p className="text-sm text-foreground-500 text-center mb-8">
                {aboutData.contact.description}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-5 rounded-xl bg-background-100 flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800">电子邮箱</h4>
                    <p className="text-sm text-foreground-500">{aboutData.contact.email}</p>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800">客服电话</h4>
                    <p className="text-sm text-foreground-500">{aboutData.contact.phone}</p>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800">公司地址</h4>
                    <p className="text-sm text-foreground-500">{aboutData.contact.address}</p>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800">微信公众号</h4>
                    <p className="text-sm text-foreground-500">{aboutData.contact.wechat}</p>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-qq-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800">QQ群</h4>
                    <p className="text-sm text-foreground-500">{aboutData.contact.qq}</p>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800">工作时间</h4>
                    <p className="text-sm text-foreground-500">{aboutData.contact.workingHours}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Contact FAQ */}
          <section id="faq" className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              联系常见问题
            </h2>
            <div className="max-w-2xl mx-auto space-y-3">
              {contactFaqData.map((item, index) => (
                <div key={index} className="rounded-xl bg-background-100 overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  >
                    <h4 className="text-sm font-medium text-foreground-800 pr-4">{item.question}</h4>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full transition-colors flex-shrink-0 ${
                      openFaqIndex === index ? 'bg-primary-100 text-primary-600' : 'bg-background-200 text-foreground-400'
                    }`}>
                      <i className={`text-sm transition-transform duration-300 ${
                        openFaqIndex === index ? 'ri-subtract-line' : 'ri-add-line'
                      }`}></i>
                    </div>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${
                    openFaqIndex === index ? 'max-h-96 pb-5 px-5' : 'max-h-0'
                  }`}>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}